import { useEffect, useState } from 'react';
import { useProfile, useUpdateProfile } from '../../api/profile.api';
import { useToast } from '../../components/ui/Toast';

/* Settings · Your profile — Sprint 16a.
   Resolves {{recruiter.title}} and {{recruiter.phone}}. Name + email come
   from JT and are read-only here. */

export function ProfileSection() {
  const { data, isLoading } = useProfile();
  const update = useUpdateProfile();
  const { addToast } = useToast();
  const [title, setTitle] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    if (data?.data) {
      setTitle(data.data.title ?? '');
      setPhone(data.data.phone ?? '');
    }
  }, [data]);

  const dirty =
    (data?.data && ((data.data.title ?? '') !== title || (data.data.phone ?? '') !== phone)) ?? false;

  const save = async () => {
    try {
      await update.mutateAsync({ title: title || null, phone: phone || null });
      addToast({ variant: 'success', title: 'Profile saved' });
    } catch {
      addToast({ variant: 'danger', title: 'Save failed' });
    }
  };

  return (
    <section
      style={{
        background: 'var(--card-bg, #fff)',
        border: '1px solid var(--card-border, #ececf2)',
        borderRadius: 'var(--card-radius, 8px)',
        padding: 24,
      }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--color-text-brand, #1e1b4b)' }}>
            Your profile
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-muted, #6b6b7d)' }}>
            Title and phone appear in template signatures via{' '}
            <code style={code()}>{'{{recruiter.title}}'}</code> and <code style={code()}>{'{{recruiter.phone}}'}</code>.
            Name and email come from your JobTetris account and are read-only here.
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || update.isPending}
          data-testid="profile-save"
          style={{
            padding: '8px 16px',
            background: dirty ? 'var(--color-action-primary, #1e1b4b)' : '#e3e3ee',
            color: dirty ? '#fff' : '#9a9aab',
            border: 'none',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: dirty ? 'pointer' : 'not-allowed',
          }}
        >
          {update.isPending ? 'Saving…' : dirty ? 'Save' : 'Saved'}
        </button>
      </header>

      {isLoading ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 640 }}>
          <Field label="Name (from JT)">
            <input type="text" value={data?.data.name ?? ''} readOnly style={{ ...input(), background: '#fafafa' }} />
          </Field>
          <Field label="Email (from JT)">
            <input type="text" value={data?.data.email ?? ''} readOnly style={{ ...input(), background: '#fafafa' }} />
          </Field>
          <Field label="Title">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Senior Recruiter"
              style={input()}
            />
          </Field>
          <Field label="Phone">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+61 400 123 456"
              style={input()}
            />
          </Field>
        </div>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted, #6b6b7d)', textTransform: 'uppercase', letterSpacing: 0.04 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function input(): React.CSSProperties {
  return {
    padding: '6px 10px',
    border: '1px solid var(--color-border-default, #ececf2)',
    borderRadius: 4,
    fontSize: 13,
    fontFamily: 'inherit',
  };
}
function code(): React.CSSProperties {
  return {
    fontFamily: 'var(--font-mono, ui-monospace, monospace)',
    fontSize: 12,
    background: 'var(--color-surface-muted, #f7f8fc)',
    padding: '1px 6px',
    borderRadius: 3,
  };
}
